import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon, ExclamationTriangleIcon, PlusIcon } from '@radix-ui/react-icons';
import { Box, Button, Callout, Card, Flex, Spinner, Text } from '@radix-ui/themes';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import { useGetCurrentAcademicYearQuery } from '../../hooks/useGetCurrentAcademicYearQuery';
import { useGetTeacheIdByUserId } from '../../hooks/useGetTeacheIdByUserId';
import { RootState } from '../../redux/store';
import { routes } from '../../routes';
import { createAssessment } from '../../services/school/assessment/createAssessment';
import { getAssessments } from '../../services/school/assessment/getAssessments';
import { updateAssessment } from '../../services/school/assessment/updateAssessment';
import { getTeacherClasses, getTeacherSubjects } from '../../services/school/teacher/getTeachers';
import { AssessmentPartial, AssessmentType } from '../../types/Assessment';
import { Assessment } from './Assessment';

const assessmentPartialSchema = yup.object().shape({
  type: yup.string().required('Tipo de avaliação é obrigatório'),
  weight: yup.number(),
  date: yup.string().required("Data de realização obrigatória")
    .test('is-date', 'Data inválida', (value) => !value || !isNaN(Date.parse(value)))
});

const createAssessmentSchema = yup.object().shape({
  idSubject: yup.string().required('Disciplina é obrigatória'),
  idClass: yup.string().required('Turma é obrigatória'),
  assessments: yup.array().of(assessmentPartialSchema).min(1, 'Pelo menos uma avaliação é necessária'),
});

export const CreateAssessment = () => {

  const defaultValues: AssessmentType = {
    assessments: [{
      type: '',
      weight: 0,
      date: '',
    }],
    idSubject: '',
    idDepartament: '',
    idFaculty: '',
    idCourse: '',
    idClass: ''
  }
  const navigate = useNavigate();
  const [weightError, setWeightError] = useState<string | null>(null);

  const mutation = useMutation({ mutationFn: createAssessment, mutationKey: 'createAssessment' });
  const mutationUpdate = useMutation({ mutationFn: updateAssessment, mutationKey: 'updateAssessment' });
  const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear } = useGetCurrentAcademicYearQuery();

  const { control, handleSubmit, setValue, getValues, formState: { errors }, watch } = useForm<AssessmentType>({
    resolver: yupResolver(createAssessmentSchema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const { fields, append, remove, } = useFieldArray({
    control,
    name: 'assessments',
  });
  const { UserId } = useSelector((state: RootState) => state.login.userData)
  const { data: teacherData } = useGetTeacheIdByUserId(UserId);
  const { isLoading: isLoadingClasses, refetch: fetchTeacherClasses, data: classes, isError: isErrorTeacherClasses } = useQuery(
    ['fetchTeacherClasses'],
    async () => {
      try {
        const classes = await getTeacherClasses({ PageNumber: 1, PageSize: 400, IdTeacher: teacherData?.idTeacher });
        return classes;
      } catch (error) {
      }
    },
    {
      enabled: !!teacherData?.idTeacher,
    }
  );
  const { isLoading: isLoadingSubjects, refetch: fetchTeacherSubjects, data: subjects, isError: isErrorTeacherSubjects } = useQuery(
    ['fetchTeacherSubjects'],
    async () => {
      try {
        const subjects = await getTeacherSubjects({ PageNumber: 1, PageSize: 400, IdTeacher: teacherData?.idTeacher, IdClass: getValues('idClass') });
        return subjects;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const { isLoading: isLoadingAssessments, refetch: fetchTeacherAssessments, data: assessments, isError: isErrorTeacherAssessments } = useQuery(
    ['fetchTeacherAssessments'],
    async () => {

      try {
        const assessments = await getAssessments({ PageNumber: 1, PageSize: 400, IdTeacher: teacherData?.idTeacher, IdClass: getValues('idClass'), IdSubject: getValues('idSubject') });
        if (assessments?.items && assessments.items.length > 0) {
          setValue('assessments', assessments?.items.map(a => {
            return {
              date: a?.date!,
              type: a?.type!,
              weight: a?.weight!,
              id: a?.id!,
              status: a?.status
            }
          }))
        }

        return assessments;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );


  if (!teacherData || !teacherData.idTeacher) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>
            Entre como docente. &nbsp;
            <Link to={routes.dashboard} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
          </Callout.Text>
        </Callout.Root>

      </Flex>
    );
  }
  if (!currentAcademicYear || !currentAcademicYear?.status) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>
            Por favor active o ano lectivo.{" "}
            <Link to={routes.academicYears} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
          </Callout.Text>
        </Callout.Root>

      </Flex>
    );
  }

  const handleChangeClass = async (idClass: string) => {
    setValue('idClass', idClass);
    const _class = classes?.items.find(i => i.id === idClass);
    setValue("semester", _class?.semester ?? 0)
    fetchTeacherSubjects()
  };
  const handleChangeSubject = (idSubject: string) => {
    setValue('idSubject', idSubject);
    setValue('assessments', [
      {
        type: '',
        weight: 0,
        date: ''
      }]);
    fetchTeacherAssessments();

  }

  const onSubmit = handleSubmit(async (data) => {
    let forEdit = data.assessments?.filter(a => a.id)
    if (forEdit && forEdit.length > 0 && assessments?.items && assessments?.items.length > 0) {
      let removedAssessments = assessments?.items.filter(
        original => !forEdit?.some(current => current.id === original.id)
      ).map(a => {
        return {
          id: a.id,
          date: a.date,
          type: a.type,
          weight: a.weight,
          status: false
        } as AssessmentPartial
      });
      forEdit = [...forEdit, ...removedAssessments]
    }
    const forSave = data.assessments?.filter(a => !a.id);

    forSave?.forEach(a => {
      if (a.type && ['Exame Normal', 'Exame de Recorrência', 'Exame Especial'].includes(a.type) && a.weight === 0) {
        a.weight = 50;
      }
    });

    forEdit?.forEach(a => {
      if (a.type && ['Exame Normal', 'Exame de Recorrência', 'Exame Especial'].includes(a.type) && a.weight === 0) {
        a.weight = 50;
      }
    });

    const totalWeight = calculateTotalWeight();
    if (totalWeight !== 100) {
      setWeightError(`A soma dos pesos deve ser exatamente 100. Atualmente é ${totalWeight}.`);
      return;
    }
    setWeightError(null);
    try {
      const assessmentsSave = forSave?.map(assessment => {
        return {
          ...data,
          ...assessment,
          idAcademicYear: currentAcademicYear?.id!,
          semester: data?.semester,
          idTeacher: teacherData?.idTeacher!,
        } as AssessmentType
      });
      const assessmentsEdit = forEdit?.map(assessment => {
        return {
          ...data,
          ...assessment,
          idAcademicYear: currentAcademicYear?.id!,
          semester: data?.semester,
          idTeacher: teacherData?.idTeacher!,
        } as AssessmentType
      });

      await Promise.all(assessmentsSave!.map((cv) => {
        return mutation.mutateAsync(cv)
      }))
      await Promise.all(assessmentsEdit!.map((cv) => {
        return mutationUpdate.mutateAsync(cv)
      }))

      toast.success("Configuração de avaliação");
      navigate(routes.assessments);

    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const handleBack = () => {
    navigate(routes.assessments);
  }

  const calculateTotalWeight = () => {
    return getValues('assessments')!.filter(a => !a.type?.includes('Exame')).reduce((total, assessment) => Number(total) + (Number(assessment.weight) || 0), 0);
  };

  const selectedSubject = watch('idSubject');
  const subject = subjects?.items.find(subject => subject.id === selectedSubject);

  return (
    <Card>
      <Flex direction='column' gap='4'>
        <Box>
          <Button type="button" variant="soft" onClick={() => append({ type: '', weight: 0, date: '' })}>
            <PlusIcon /> Adicionar teste
          </Button>
        </Box>
        <form onSubmit={onSubmit}>
          <Flex gap='3' direction='column'>
            <Card size='1'>
              <Flex direction='column' gap="2">
                <Flex gap='3'>
                  <SelectComponent name="idClass" label="Turma" control={control} errors={errors} style={{ width: '100%' }}
                    options={classes?.items.map(_class => {
                      return {
                        label: _class.description,
                        value: _class.id ?? ""
                      }
                    })}
                    onSelect={handleChangeClass}
                    itemsLoading={isLoadingClasses}
                    itemsError={isErrorTeacherClasses}
                  />
                  <SelectComponent name="idSubject" label="Disciplina" control={control} errors={errors} style={{ width: '100%' }}
                    options={subjects?.items.map(subject => {
                      return {
                        label: subject.description,
                        value: subject.id ?? ""
                      }
                    })}
                    itemsLoading={isLoadingSubjects}
                    itemsError={isErrorTeacherSubjects}
                    onSelect={handleChangeSubject}
                  />
                </Flex>
              </Flex>
            </Card>
            {fields.map((field, index) => (
              <Assessment
                key={field.id}
                index={index}
                control={control}
                errors={errors}
                setValue={setValue}
                watch={watch}
                remove={() => remove(index)}
                subject={subject}
              />
            ))}
            {errors?.assessments?.message && (
              <Text color="red" size="1">{`${errors?.assessments?.message}`}</Text>
            )}
            <Box>
              <Button type="button" variant="soft" onClick={() => append({ type: '', weight: 0, date: '' })}>
                <PlusIcon /> Adicionar teste
              </Button>
            </Box>
            {weightError && (
              <Text color="red" size="2">
                {`${weightError}%`}
              </Text>
            )}
            <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
              <Button type="button" variant="soft" color="gray" disabled={mutation.isLoading} onClick={() => {
                handleBack();
              }}
              >
                <ChevronLeftIcon /> Voltar
              </Button>
              <Button type='submit' variant='surface'
                disabled={mutation.isLoading}

              >
                {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                Registar
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </Card>
  )
}
